import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Global } from "@emotion/react";
import {
  callSurveyAPI,
  checkNameSurvey,
  checkRegisteredMember,
  handleAsync,
  handlePaySurvey,
  handleSurveySubmit,
  pollTransactionByRequestId,
} from "../utils/api";
import { handleInputSurvey, NOT_REGISTERED_MESSAGE } from "../utils/functions";
import {
  getTemplateActionWord,
  isRegistrationSurveyName,
} from "../utils/templateWords";
import { getVisibleQuestions, getValidationErrors } from "../utils/surveyRules";
import AttendanceTemplate from "./Features/AttendanceTemplate";
import BusinessItemsView from "./Features/BusinessItemsView";
import FourOFour from "./404";

// Standalone, Google-Forms-style page for /forms/:surveyId. The group/org the
// response belongs to come from the survey response. When the survey has a
// payment question, the sale data is loaded with the group id
// (business/sales/data/{group_id}) to get the payment methods and sale details.

const ACCENT = "#524fdb";
const PAGE_BG = "#f0ebf8";
const BORDER = "#dadce0";
const ERROR = "#d93025";
const TEXT = "#202124";
const MUTED = "#5f6368";
const FONT = "'Google Sans', Roboto, Arial, sans-serif";

const isPaymentQuestion = (q) => {
  if (q.question_type === "payment") return true;
  return (
    (q.question || "").toLowerCase().startsWith("payment") &&
    (!q.question_type || q.question_type === "text")
  );
};

// "payment#500", "payment-card#Tithe#500", ... (same format as the survey modal)
const parsePaymentQuestion = (q) => {
  const source =
    (q.question || "").toLowerCase().startsWith("payment") &&
    (q.question || "").includes("#")
      ? q.question
      : q.question_options || "";
  const parts = source.split("#");
  if (parts.length <= 1) {
    return {
      amount: "",
      selection: "",
      isDefaultOverride: false,
      explicitCard: false,
      explicitMpesa: false,
    };
  }
  const typePart = parts[0].toLowerCase();
  let amount = "";
  let selection = "";
  if (!isNaN(parts[1])) amount = parseInt(parts[1], 10);
  else selection = parts[1];
  if (parts.length >= 3) amount = parseInt(parts[2], 10) || "";
  return {
    amount,
    selection,
    isDefaultOverride: parts[3] === "Default",
    explicitCard: typePart.includes("card"),
    explicitMpesa: typePart.includes("mpesa"),
  };
};

const SELECT_TYPES = [
  "select",
  "dropdown",
  "multiple_choice",
  "tiles",
  "linear_scale",
  "rating",
];
const ITEMS_TYPES = ["options", "checkboxes"];

// sale_settings.pay_details_targets -> [{ value, target, suggested, subCategories }]
// target: amount that locks the field (0 for internal targets)
// suggested: amount to pre-fill when targets are internal; payer can edit it
const parseTargets = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw.includes("'") ? raw.replace(/'/g, '"') : raw);
    const isInternal = parsed?.isInternalTargets === true;
    return (parsed?.targets || [])
      .filter((t) => (t.name || t.value)?.trim())
      .map((t) => ({
        ...t,
        value: t.name || t.value,
        target: isInternal ? 0 : t.amount || t.target,
        suggested: isInternal ? Number(t.amount || t.target) || 0 : 0,
      }));
  } catch (e) {
    return [];
  }
};

const capitalizeFirstLetter = (str) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

const getOptions = (q) =>
  (q.question_options || "")
    .split(/,\s*/i)
    .map((o) => o.trim().toLowerCase())
    .filter(Boolean);

const Card = React.forwardRef(({ children, hasError, ...rest }, ref) => (
  <Box
    ref={ref}
    bg="white"
    border="1px solid"
    borderColor={hasError ? ERROR : BORDER}
    borderRadius="8px"
    p="24px"
    mb="12px"
    {...rest}
  >
    {children}
  </Box>
));

const Label = ({ children, required }) => (
  <Text fontSize="16px" color={TEXT} mb="16px" whiteSpace="pre-wrap">
    {children}
    {required && <span style={{ color: ERROR }}> *</span>}
  </Text>
);

const FieldError = ({ message }) =>
  message ? (
    <Text fontSize="12px" color={ERROR} mt="12px">
      {message}
    </Text>
  ) : null;

const underlineInput = {
  display: "block",
  width: "100%",
  maxWidth: "50%",
  minWidth: "220px",
  border: "none",
  borderBottom: `1px solid ${BORDER}`,
  borderRadius: 0,
  outline: "none",
  padding: "6px 0",
  fontSize: "14px",
  fontFamily: FONT,
  color: TEXT,
  background: "transparent",
};

const UnderlineInput = ({ as: Tag = "input", style, ...props }) => (
  <Tag
    {...props}
    style={{ ...underlineInput, ...style }}
    onFocus={(e) => (e.target.style.borderBottom = `2px solid ${ACCENT}`)}
    onBlur={(e) => (e.target.style.borderBottom = `1px solid ${BORDER}`)}
  />
);

const selectStyle = (hasValue) => ({
  border: `1px solid ${BORDER}`,
  borderRadius: "4px",
  padding: "14px 12px",
  minWidth: "220px",
  fontSize: "14px",
  fontFamily: FONT,
  color: hasValue ? TEXT : MUTED,
  background: "white",
});

const Choice = ({ type, checked, onChange, children }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "14px",
      padding: "8px 0",
      cursor: "pointer",
      fontSize: "14px",
      color: TEXT,
    }}
  >
    <input
      type={type}
      checked={checked}
      onChange={onChange}
      style={{ width: 20, height: 20, accentColor: ACCENT, cursor: "pointer" }}
    />
    {children}
  </label>
);

const QuestionInput = ({ question, answers, onChange, onToggle }) => {
  const idx = question.question_index;
  const value = answers[idx];
  const options = getOptions(question);

  switch (question.question_type) {
    case "text":
    case "short_answer":
      return (
        <UnderlineInput
          type="text"
          placeholder="Your answer"
          value={value || ""}
          onChange={(e) => onChange(idx, e.target.value)}
        />
      );
    case "paragraph":
      return (
        <UnderlineInput
          as="textarea"
          rows={3}
          placeholder="Your answer"
          value={value || ""}
          onChange={(e) => onChange(idx, e.target.value)}
          style={{ resize: "vertical" }}
        />
      );
    case "date":
    case "time":
      return (
        <UnderlineInput
          type={question.question_type}
          value={value || ""}
          onChange={(e) => onChange(idx, e.target.value)}
          style={{ maxWidth: "220px" }}
        />
      );
    case "dropdown":
      return (
        <select
          value={value || ""}
          onChange={(e) => onChange(idx, e.target.value)}
          style={{
            border: `1px solid ${BORDER}`,
            borderRadius: "4px",
            padding: "14px 12px",
            minWidth: "220px",
            fontSize: "14px",
            fontFamily: FONT,
            color: value ? TEXT : MUTED,
            background: "white",
          }}
        >
          <option value="">Choose</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {capitalizeFirstLetter(o)}
            </option>
          ))}
        </select>
      );
    case "multiple_choice":
    case "select":
    case "options":
      return (
        <Box>
          {options.map((o) => (
            <Choice
              key={o}
              type="radio"
              checked={value === o}
              onChange={() => onChange(idx, o)}
            >
              {capitalizeFirstLetter(o)}
            </Choice>
          ))}
        </Box>
      );
    case "checkboxes":
      return (
        <Box>
          {options.map((o) => (
            <Choice
              key={o}
              type="checkbox"
              checked={(value || []).includes(o)}
              onChange={() => onToggle(idx, o)}
            >
              {capitalizeFirstLetter(o)}
            </Choice>
          ))}
        </Box>
      );
    case "linear_scale":
    case "rating": {
      const scale =
        question.question_type === "rating" || options.length === 0
          ? ["1", "2", "3", "4", "5"]
          : options;
      return (
        <Box display="flex" flexWrap="wrap" gap="24px">
          {scale.map((o) => (
            <label
              key={o}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                color: TEXT,
                cursor: "pointer",
              }}
            >
              {o}
              <input
                type="radio"
                checked={String(value) === o}
                onChange={() =>
                  onChange(idx, question.question_type === "rating" ? Number(o) : o)
                }
                style={{ width: 20, height: 20, accentColor: ACCENT }}
              />
            </label>
          ))}
        </Box>
      );
    }
    case "consent": {
      const raw = (question.question_options || "").trim();
      const checkboxLabel = raw && raw.toLowerCase() !== "none" ? raw : "";
      return (
        <Choice
          type="checkbox"
          checked={value === "Yes"}
          onChange={(e) => onChange(idx, e.target.checked ? "Yes" : "")}
        >
          {checkboxLabel || "I agree"}
        </Choice>
      );
    }
    default:
      return null;
  }
};

const FormPage = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound
  const [answers, setAnswers] = useState({});
  const [number, setNumber] = useState("");
  const [phoneLength, setPhoneLength] = useState(10);
  const [name, setName] = useState("");
  const [contactId, setContactId] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [disabled, setDisabled] = useState(false); // contact lookup in flight
  const [isRegisteredMember, setIsRegisteredMember] = useState(false);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [sale, setSale] = useState(null);
  const [saleStatus, setSaleStatus] = useState("loading"); // loading | ready | failed
  const [amount, setAmount] = useState("");
  const [payType, setPayType] = useState("mpesa");
  const [currencyMode, setCurrencyMode] = useState("BASE");
  // M-Pesa: null | "processing" | "paid" | "failed" | "timeout"
  const [paymentState, setPaymentState] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [selection, setSelection] = useState("");
  const [selectionDetails, setSelectionDetails] = useState([]);
  const cardRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    callSurveyAPI(surveyId)
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.data?.data?.survey_questions) {
          setSurvey(result.data.data);
          setStatus("ready");
        } else {
          setStatus("notfound");
        }
      })
      .catch(() => !cancelled && setStatus("notfound"));
    return () => {
      cancelled = true;
    };
  }, [surveyId]);

  const orgId = survey?.org_id;
  const groupId = survey?.group_id;
  const isAttendance = survey?.survey_template === "attendance";

  const paymentQuestion = useMemo(
    () => (survey?.survey_questions || []).find(isPaymentQuestion),
    [survey]
  );
  const paymentConfig = useMemo(
    () => (paymentQuestion ? parsePaymentQuestion(paymentQuestion) : null),
    [paymentQuestion]
  );

  // The group's sale supplies the paylink settings the survey modal relies on
  // (labels, registered-only, id tag, payment methods, targets, currencies).
  useEffect(() => {
    if (!survey) return;
    if (!groupId) {
      setSaleStatus("failed");
      return;
    }
    let cancelled = false;
    setSaleStatus("loading");
    handleAsync(groupId)
      .then((result) => {
        if (cancelled) return;
        setSale(result);
        setSaleStatus("ready");
      })
      .catch(() => !cancelled && setSaleStatus("failed"));
    return () => {
      cancelled = true;
    };
  }, [survey, groupId]);

  // Values coming from the payment question itself
  useEffect(() => {
    if (!paymentConfig) return;
    setAmount(paymentConfig.amount);
    setSelection(paymentConfig.selection || "");
    if (paymentConfig.explicitCard) setPayType("card");
  }, [paymentConfig]);

  const saledata = sale?.data?.data?.saledata;
  const saleSettings = saledata?.sale_settings;
  const paymentData = sale?.paymentData;
  const hasCard = !!paymentData?.some((p) => p?.link_type === "CARD");
  const hasMpesa = !!paymentData?.some((p) => p?.link_type !== "CARD");
  const showMethodToggle =
    hasCard && hasMpesa && !paymentConfig?.explicitCard && !paymentConfig?.explicitMpesa;

  // Only card is activated: use it
  useEffect(() => {
    if (saleStatus === "ready" && hasCard && !hasMpesa) setPayType("card");
  }, [saleStatus, hasCard, hasMpesa]);

  // Plan expired / link not active (same message as the survey modal)
  const serviceUnavailable =
    saleStatus === "ready" && (sale?.payLink === undefined || !sale?.subActive);

  // Settings
  const templateType = saleSettings?.template_type || "default";
  const hideAnonymousOption = ["welfare", "chamas"].includes(templateType);
  const actionWord = getTemplateActionWord(templateType);
  const nameLabel = saleSettings?.name_label || "Name";
  const isRegistrationSurvey = isRegistrationSurveyName(survey?.survey_name);
  const registeredOnlySetting =
    saleSettings?.registered_only === true ||
    saleSettings?.registered_only === 1 ||
    saleSettings?.registered_only === "true";
  // Registration forms stay open to new contacts
  const registeredOnly = registeredOnlySetting && !isRegistrationSurvey;
  const idtagRequired = !!saleSettings?.idtag_required && !isRegistrationSurvey;
  const idtagLabel = saleSettings?.idtag_label || "ID Tag";
  const nameParts = (name || "").split("#");
  const baseName =
    nameParts.length > 1 ? nameParts.slice(0, -1).join("#").trim() : nameParts[0] || "";
  const idTag = nameParts.length > 1 ? nameParts[nameParts.length - 1].trim() : "";
  const lookupsEnabled =
    !saledata || ["Collection", "Wallet"].includes(saledata?.sale_type);

  let phoneButtonConfig = null;
  const phoneButtonLabelRaw = saleSettings?.phone_button_label;
  if (phoneButtonLabelRaw) {
    try {
      phoneButtonConfig = JSON.parse(phoneButtonLabelRaw);
    } catch (e) {
      phoneButtonConfig = null;
    }
  }
  const phoneLabel =
    payType === "card"
      ? phoneButtonConfig?.cardPrompt ||
        (!phoneButtonConfig && phoneButtonLabelRaw) ||
        "Phone number"
      : phoneButtonConfig?.mpesaPrompt || "Phone number";

  // Multicurrency ("KES#USD#130")
  const [baseCurrency, targetCurrency, conversionRate] = saleSettings?.multicurrency
    ? saleSettings.multicurrency.split("#")
    : [null, null, null];
  const hasMulticurrency = !!(baseCurrency && targetCurrency && conversionRate);
  const useTarget = payType === "card" && currencyMode === "TARGET" && hasMulticurrency;
  const currencyLabel = useTarget ? targetCurrency : baseCurrency || "KES";

  // Payment targets
  const detailType = saleSettings?.pay_detail_type;
  const urlOptions = useMemo(
    () => parseTargets(saleSettings?.pay_details_targets),
    [saleSettings]
  );
  const selectMode =
    SELECT_TYPES.includes(detailType) && !paymentConfig?.isDefaultOverride;
  const itemsMode =
    ITEMS_TYPES.includes(detailType) && !paymentConfig?.isDefaultOverride;
  const selectedCategory = selection ? String(selection).split("#")[0] : "";
  const selectedSubCategory = selection
    ? String(selection).split("#").slice(1).join("#")
    : "";
  const selectedTarget = urlOptions.find((t) => t.value === selectedCategory);
  const hasPredefinedTarget = !!selectedTarget && selectedTarget.target > 0;
  const subCategoryOptions = (selectedTarget?.subCategories || "")
    .split(",")
    .map((sub) => sub.trim())
    .filter(Boolean);
  const needsSubCategory =
    selectMode && subCategoryOptions.length > 0 && !selectedSubCategory;
  const isPresetAmount = !!paymentConfig?.amount;
  const defaultOptions = sale?.defaultOptions || [];

  // Picking a category: a real target sets (and locks) the amount; an internal
  // target pre-fills it but the payer can edit; none leaves it to the payer.
  // Keyed on the category so choosing a subcategory doesn't reset an edit.
  useEffect(() => {
    if (!selectMode || !selectedCategory) return;
    if (selectedTarget && selectedTarget.target > 0) {
      setAmount(parseInt(selectedTarget.target));
    } else if (selectedTarget) {
      setAmount(selectedTarget.suggested > 0 ? selectedTarget.suggested : "");
    }
  }, [selectMode, selectedCategory, selectedTarget]);

  const paymentUnavailable =
    !!paymentConfig &&
    (saleStatus === "failed" || (saleStatus === "ready" && !paymentData?.length));
  const paymentPending = !!paymentConfig && !(saleStatus === "ready" && paymentData?.length);
  const amountMissing =
    !!paymentConfig && (!(Number(amount) >= 1) || needsSubCategory);

  // Contact lookup once the phone number is complete (not for registered-only
  // collections, which verify membership instead).
  useEffect(() => {
    if (
      registeredOnly ||
      !orgId ||
      isAttendance ||
      !lookupsEnabled ||
      saleStatus === "loading" ||
      number?.length !== phoneLength
    ) {
      return;
    }
    let cancelled = false;
    setContactId("");
    setName("");
    setDisabled(true);
    checkNameSurvey(orgId, number)
      .then((result) => {
        if (cancelled) return;
        setName(result.name || "");
        setContactId(result.contactId || "");
      })
      .catch(() => {})
      .finally(() => !cancelled && setDisabled(false));
    return () => {
      cancelled = true;
    };
  }, [number, phoneLength, orgId, isAttendance, registeredOnly, lookupsEnabled, saleStatus]);

  // registered_only: the field holds a phone number (checked at full length) or
  // free text such as an email (checked after typing pauses).
  useEffect(() => {
    if (!registeredOnly || !groupId || !lookupsEnabled) return;
    const value = (number || "").trim();
    const isPhoneLike = /^\d+$/.test(value);
    const isReady = isPhoneLike ? value.length === phoneLength : value.length >= 4;

    setIsRegisteredMember(false);
    setContactId("");
    if (!isReady) return;

    let cancelled = false;
    setDisabled(true);
    const handle = setTimeout(
      async () => {
        const result = await checkRegisteredMember(groupId, value);
        if (cancelled) return;
        setIsRegisteredMember(result.found);
        setName(result.found ? result.name || (!sale?.nameOption ? "anonymous" : "") : "");
        setContactId(result.found ? result.contactId : "");
        setDisabled(false);
      },
      isPhoneLike ? 0 : 600
    );
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [number, phoneLength, registeredOnly, groupId, lookupsEnabled, sale]);

  // Registration forms: warn when the phone/email already belongs to a contact
  useEffect(() => {
    const value = (number || "").trim();
    const isPhoneLike = /^\d+$/.test(value);
    const isReady = isPhoneLike ? value.length === phoneLength : value.length >= 4;

    setExistingRegistration(null);
    if (!isRegistrationSurvey || !groupId || !isReady) return;

    let cancelled = false;
    const handle = setTimeout(
      async () => {
        const result = await checkRegisteredMember(groupId, value);
        if (!cancelled && result.found) setExistingRegistration({ name: result.name });
      },
      isPhoneLike ? 0 : 600
    );
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [number, phoneLength, isRegistrationSurvey, groupId]);

  const questions = useMemo(
    () =>
      (survey?.survey_questions || []).filter((q) => !isPaymentQuestion(q)),
    [survey]
  );
  const visibleQuestions = getVisibleQuestions(questions, answers);
  const validationErrors = getValidationErrors(visibleQuestions, answers);

  const handleAnswerChange = (idx, value) =>
    setAnswers((prev) => (prev[idx] === value ? prev : { ...prev, [idx]: value }));

  const handleToggle = (idx, option) =>
    setAnswers((prev) => {
      const current = prev[idx] || [];
      return {
        ...prev,
        [idx]: current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option],
      };
    });

  const hasAnswer = (a) => (Array.isArray(a) ? a.length > 0 : !!a);
  const phoneComplete = number?.length === phoneLength;
  const notRegistered = registeredOnly && !disabled && !isRegisteredMember;
  const phoneError = isAttendance
    ? ""
    : !phoneComplete
      ? "This is a required question"
      : notRegistered
        ? NOT_REGISTERED_MESSAGE
        : "";
  const nameMissing = !isAttendance && !anonymous && !name.trim();
  const idTagMissing = !isAttendance && idtagRequired && !idTag.trim();

  const errorFor = (q) => {
    if (q.question_required !== false && !hasAnswer(answers[q.question_index])) {
      return "This is a required question";
    }
    return validationErrors[q.question_index] || "";
  };

  const handleSubmit = async () => {
    setSubmitError("");
    const firstInvalid = visibleQuestions.find((q) => errorFor(q));
    if (phoneError || nameMissing || idTagMissing || firstInvalid || amountMissing) {
      setShowErrors(true);
      const target = phoneError
        ? cardRefs.current.phone
        : nameMissing
          ? cardRefs.current.name
          : idTagMissing
            ? cardRefs.current.idtag
            : firstInvalid
              ? cardRefs.current[firstInvalid.question_index]
              : cardRefs.current.payment;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    const contactExists = contactId?.length > 0;
    const payerName = anonymous ? "Anonymous" : name;
    const response_data = visibleQuestions
      .filter((q) => answers[q.question_index] !== undefined)
      .map((q) => {
        const a = answers[q.question_index];
        return {
          question_index: String(q.question_index),
          response: Array.isArray(a) ? a.join(", ") : String(a),
        };
      });

    // Trigger the payment first, then record the response with its request id
    let requestId = "";
    let cardUrl = "";
    if (paymentQuestion) {
      const finalAmount = useTarget
        ? Math.round(amount / parseFloat(conversionRate))
        : amount;
      const pay = await handlePaySurvey({
        org_id: saledata?.org_id || orgId,
        sales_code: saledata?.sale_trx_code,
        amount: finalAmount,
        number,
        paymentData,
        name: payerName,
        payType,
        selection,
        selection_details: itemsMode ? selectionDetails : [],
        saleId: saledata?.id,
      });
      if (pay?.authorization_url) {
        cardUrl = pay.authorization_url;
        requestId = pay.request_id || "Initiated Card Payment";
      } else if (pay?.success) {
        requestId = String(pay.requestId || "");
      } else {
        setSubmitting(false);
        setSubmitError(pay?.message || "Payment trigger failed. Please try again.");
        return;
      }
      response_data.push({
        question_index: String(paymentQuestion.question_index),
        response: requestId,
      });
    }

    const result = await handleSurveySubmit({
      group_id: groupId,
      survey_id: survey.id,
      contact_exists: contactExists,
      contact_id: contactExists ? contactId : orgId,
      contact_name: `${payerName}, ${number}`,
      response_data,
    });
    setSubmitting(false);

    if (!result.success) {
      setSubmitError(result.message || "an error occurred! please retry");
      return;
    }
    setSubmitted(true);
    window.scrollTo({ top: 0 });
    if (cardUrl) {
      setCheckoutUrl(cardUrl);
      return;
    }
    if (paymentQuestion && payType === "mpesa" && requestId) {
      setPaymentState("processing");
      const outcome = await pollTransactionByRequestId(requestId, amount);
      setPaymentState(outcome.status);
    }
  };

  if (status === "notfound") return <FourOFour />;

  const pageStyles = (
    <Global
      styles={`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');
        body { background-color: ${PAGE_BG} !important; }
      `}
    />
  );

  if (status === "loading" || saleStatus === "loading") {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        {pageStyles}
        <Spinner color={ACCENT} size="xl" thickness="4px" />
      </Box>
    );
  }

  const closed = survey.survey_status === 0;

  const headerCard = (
    <Card
      p={0}
      borderTop={`10px solid ${ACCENT}`}
      borderTopRadius="8px"
      overflow="hidden"
    >
      <Box p="24px 24px 20px">
        <Text
          as="h1"
          fontSize={{ base: "28px", md: "32px" }}
          lineHeight="1.25"
          color={TEXT}
          mb={survey.survey_description ? "16px" : 0}
        >
          {survey.survey_name || "Untitled form"}
        </Text>
        {survey.survey_description && (
          <Text fontSize="14px" color={TEXT} whiteSpace="pre-wrap">
            {survey.survey_description}
          </Text>
        )}
      </Box>
      {!submitted && !closed && !serviceUnavailable && (
        <Box borderTop={`1px solid ${BORDER}`} p="14px 24px">
          <Text fontSize="14px" color={ERROR}>
            * Indicates required question
          </Text>
        </Box>
      )}
    </Card>
  );

  const showAnonymous = !hideAnonymousOption;

  let body;
  if (serviceUnavailable) {
    body = (
      <Card>
        <Text fontSize="14px" color={TEXT}>
          Unfortunately, this service is temporarily unavailable as the organizer's
          plan needs to be renewed. Please check back later or contact the organizer
          for further assistance. We sincerely apologize for the inconvenience.
        </Text>
      </Card>
    );
  } else if (closed) {
    body = (
      <Card>
        <Text fontSize="14px" color={TEXT}>
          This form is no longer accepting responses.
        </Text>
      </Card>
    );
  } else if (submitted) {
    body = (
      <Card>
        {paymentState === "processing" && (
          <Box display="flex" alignItems="center" gap="12px" mb="16px">
            <Spinner color={ACCENT} size="sm" />
            <Text fontSize="14px" color={TEXT}>
              Processing your payment. Check your phone and enter your M-Pesa PIN,
              and keep this page open.
            </Text>
          </Box>
        )}
        <Text fontSize="14px" color={TEXT} whiteSpace="pre-wrap">
          {paymentState === "failed"
            ? "Your response was saved, but the payment was not completed."
            : paymentState === "timeout"
              ? "Your response was saved. We could not confirm the payment yet - if you paid, it will reflect shortly."
              : `${paymentState === "paid" ? "Payment complete. " : ""}${
                  survey.survey_on_submit_message || "Your response has been recorded."
                }`}
        </Text>
      </Card>
    );
  } else {
    body = (
      <>
        {isAttendance ? (
          <Card>
            <AttendanceTemplate
              questions={{ data: survey }}
              answers={answers}
              handleAnswerChange={handleAnswerChange}
              setTriggerSurvey={() => {}}
              setName={setName}
              setNumber={setNumber}
            />
          </Card>
        ) : (
          <>
            <Card
              ref={(el) => (cardRefs.current.phone = el)}
              hasError={showErrors && !!phoneError}
            >
              <Label required>{phoneLabel}</Label>
              <UnderlineInput
                type="text"
                placeholder="Your answer"
                value={number}
                onChange={(e) => handleInputSurvey(e, setPhoneLength, setNumber)}
              />
              {registeredOnly && phoneComplete && notRegistered && !showErrors && (
                <FieldError message={NOT_REGISTERED_MESSAGE} />
              )}
              {existingRegistration && (
                <Box
                  mt="16px"
                  p="12px"
                  borderRadius="4px"
                  bg="orange.50"
                  borderLeft="4px solid"
                  borderColor="orange.400"
                >
                  <Text fontSize="14px" color="orange.800">
                    {existingRegistration.name
                      ? `You are already registered as '${existingRegistration.name}', do you want to continue?`
                      : "You are already registered, do you want to continue?"}
                  </Text>
                </Box>
              )}
              {showErrors && <FieldError message={phoneError} />}
            </Card>

            {phoneComplete && (
              <>
                <Card
                  ref={(el) => (cardRefs.current.name = el)}
                  hasError={showErrors && nameMissing}
                >
                  <Label required>{nameLabel}</Label>
                  <UnderlineInput
                    type="text"
                    placeholder="Your answer"
                    value={anonymous ? "Anonymous" : baseName}
                    disabled={anonymous || disabled}
                    onChange={(e) =>
                      setName(idTag ? `${e.target.value}#${idTag}` : e.target.value)
                    }
                  />
                  {showAnonymous && (
                    <Box mt="12px">
                      <Choice
                        type="checkbox"
                        checked={anonymous}
                        onChange={() => setAnonymous(!anonymous)}
                      >
                        Fill anonymously
                      </Choice>
                    </Box>
                  )}
                  {showErrors && nameMissing && (
                    <FieldError message="This is a required question" />
                  )}
                </Card>
                {idtagRequired && (
                  <Card
                    ref={(el) => (cardRefs.current.idtag = el)}
                    hasError={showErrors && idTagMissing}
                  >
                    <Label required>{idtagLabel}</Label>
                    <UnderlineInput
                      type="text"
                      placeholder="Your answer"
                      value={anonymous ? "" : idTag}
                      disabled={disabled}
                      onChange={(e) =>
                        setName(
                          e.target.value ? `${baseName}#${e.target.value}` : baseName
                        )
                      }
                    />
                    {showErrors && idTagMissing && (
                      <FieldError message="This is a required question" />
                    )}
                  </Card>
                )}
              </>
            )}
          </>
        )}

        {visibleQuestions.map((q) => {
          const error = showErrors ? errorFor(q) : "";
          return (
            <Card
              key={q.question_index}
              ref={(el) => (cardRefs.current[q.question_index] = el)}
              hasError={!!error}
            >
              <Label required={q.question_required !== false}>
                {q.question}
              </Label>
              <QuestionInput
                question={q}
                answers={answers}
                onChange={handleAnswerChange}
                onToggle={handleToggle}
              />
              <FieldError message={error} />
            </Card>
          );
        })}

        {paymentQuestion && (
          <Card
            ref={(el) => (cardRefs.current.payment = el)}
            hasError={showErrors && amountMissing}
          >
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <Label required>
                {capitalizeFirstLetter(paymentQuestion.question.split("#")[0] || "Payment")}
              </Label>
              {payType === "card" && hasMulticurrency && (
                <Button
                  size="xs"
                  variant="ghost"
                  color={ACCENT}
                  onClick={() =>
                    setCurrencyMode((prev) => (prev === "BASE" ? "TARGET" : "BASE"))
                  }
                >
                  Swap to {currencyMode === "BASE" ? targetCurrency : baseCurrency}
                </Button>
              )}
            </Box>
            {paymentUnavailable && (
              <Text fontSize="14px" color={ERROR}>
                Payment is not available for this form right now.
              </Text>
            )}
            {!paymentUnavailable && (
              <>
                {showMethodToggle && (
                  <Box mb="16px">
                    <Text fontSize="14px" color={MUTED}>
                      Choose payment method
                    </Text>
                    {["mpesa", "card"].map((v) => (
                      <Choice
                        key={v}
                        type="radio"
                        checked={payType === v}
                        onChange={() => setPayType(v)}
                      >
                        {v === "mpesa" ? "M-Pesa" : "Card"}
                      </Choice>
                    ))}
                  </Box>
                )}
                {selectMode && (
                  <Box mb="16px">
                    <Text fontSize="14px" color={MUTED} mb="8px">
                      {saleSettings?.pay_detail_button_name || "Select"}
                    </Text>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelection(e.target.value)}
                      style={selectStyle(!!selectedCategory)}
                    >
                      <option value="">Choose</option>
                      {urlOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.value}
                        </option>
                      ))}
                    </select>
                    {subCategoryOptions.length > 0 && (
                      <Box mt="16px">
                        <Text fontSize="14px" color={MUTED} mb="8px">
                          Subcategory
                        </Text>
                        <select
                          value={selectedSubCategory}
                          onChange={(e) =>
                            setSelection(`${selectedCategory}#${e.target.value}`)
                          }
                          style={selectStyle(!!selectedSubCategory)}
                        >
                          <option value="">Select a subcategory</option>
                          {subCategoryOptions.map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                        </select>
                      </Box>
                    )}
                  </Box>
                )}
                {itemsMode && (
                  <Box mb="16px">
                    <Text fontSize="14px" color={MUTED} mb="8px">
                      {saleSettings?.pay_detail_button_name || "Select"} - select one
                      or multiple items to proceed
                    </Text>
                    <BusinessItemsView
                      urlOptions={urlOptions}
                      colorMain={ACCENT}
                      setSelectionDetails={setSelectionDetails}
                      setIsSelectButtonDisabled={() => {}}
                      setTotalSplitAmountB={() => {}}
                      amount={amount}
                      setAmount={setAmount}
                      setAnyChecked={() => {}}
                      currencyText={currencyLabel}
                    />
                  </Box>
                )}
                {!itemsMode && (
                  <>
                    {defaultOptions.length > 0 && !isPresetAmount && !selectMode && (
                      <Box display="flex" flexWrap="wrap" gap="8px" mb="16px">
                        {defaultOptions.map((opt) => {
                          const val = parseInt(opt, 10);
                          return (
                            <Button
                              key={opt}
                              size="sm"
                              variant={Number(amount) === val ? "solid" : "outline"}
                              bg={Number(amount) === val ? ACCENT : "transparent"}
                              color={Number(amount) === val ? "white" : ACCENT}
                              borderColor={ACCENT}
                              borderRadius="full"
                              _hover={{ opacity: 0.85 }}
                              onClick={() => setAmount(val)}
                            >
                              {currencyLabel} {Number(val).toLocaleString()}
                            </Button>
                          );
                        })}
                      </Box>
                    )}
                    <Box display="flex" alignItems="baseline" gap="8px">
                      <Text fontSize="14px" color={MUTED}>
                        {currencyLabel}
                      </Text>
                      <UnderlineInput
                        type="number"
                        min="1"
                        placeholder="Amount"
                        value={amount}
                        readOnly={selectMode ? hasPredefinedTarget : isPresetAmount}
                        onChange={(e) =>
                          setAmount(
                            e.target.value === "" ? "" : Math.round(e.target.value)
                          )
                        }
                        style={{ maxWidth: "200px", minWidth: "120px" }}
                      />
                    </Box>
                    {hasMulticurrency && amount > 0 && payType === "card" && (
                      <Text fontSize="12px" color={MUTED} mt="8px">
                        ≈{" "}
                        {useTarget
                          ? Math.round(amount / conversionRate)
                          : Math.round(amount * conversionRate)}{" "}
                        {useTarget ? baseCurrency : targetCurrency}
                      </Text>
                    )}
                  </>
                )}
                <Text fontSize="12px" color={MUTED} mt="12px">
                  {payType === "card"
                    ? "A secure card checkout will open to complete your payment."
                    : "You will receive an STK Push on your phone to complete payment."}
                </Text>
                {showErrors && amountMissing && (
                  <FieldError
                    message={
                      needsSubCategory
                        ? "Select a subcategory"
                        : "Enter an amount to pay"
                    }
                  />
                )}
              </>
            )}
          </Card>
        )}

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="12px"
          mt="16px"
          mb="48px"
        >
          <Button
            onClick={handleSubmit}
            isLoading={submitting}
            isDisabled={paymentPending}
            bg={ACCENT}
            color="white"
            borderRadius="4px"
            px="24px"
            fontWeight="500"
            fontSize="14px"
            _hover={{ opacity: 0.9 }}
            _active={{ opacity: 0.8 }}
          >
            {paymentQuestion
              ? Number(amount) >= 1
                ? `${actionWord} ${currencyLabel} ${Number(amount).toLocaleString()}`
                : `${actionWord} and Submit`
              : "Submit"}
          </Button>
          {submitError && (
            <Text fontSize="14px" color={ERROR}>
              {submitError}
            </Text>
          )}
        </Box>
      </>
    );
  }

  return (
    <Box minH="100vh" fontFamily={FONT} py={{ base: "12px", md: "24px" }} px="12px">
      {pageStyles}
      <Box maxW="640px" mx="auto">
        {headerCard}
        {body}
        <Text textAlign="center" fontSize="12px" color={MUTED} pb="24px">
          Powered by Nobuk Collect
        </Text>
      </Box>

      <Modal
        isOpen={!!checkoutUrl}
        onClose={() => setCheckoutUrl(null)}
        closeOnOverlayClick={false}
        isCentered
        size="xl"
      >
        <ModalOverlay />
        <ModalContent p={0}>
          <ModalHeader>Enter card details to pay</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            <iframe
              src={checkoutUrl}
              title="Card Checkout"
              height="500"
              width="100%"
              frameBorder="0"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FormPage;
